import { API_BASE, GOOGLE_CLIENT_ID } from '../config';
import { CrazyGames } from './crazygames';

/** Minimal account info (display only; the server re-verifies the credential). */
export interface GoogleUser {
  sub: string;   // stable account id (Google `sub`, or local `u:<uuid>`)
  name: string;
  picture: string;
  email?: string;
}

/** Auth body attached to every authed API call: Google ID token OR local session token. */
export type AuthBody = { idToken: string } | { token: string };

const BASE = API_BASE;
const TOKEN_KEY = 'zoolympics:auth';
const GEVER_KEY = 'zoolympics:gever'; // "this device has used Google before" → silent re-auth on open

// Just the slice of the Google Identity Services API we use, typed locally (no @types dep).
interface GsiId {
  initialize(cfg: { client_id: string; callback: (r: { credential: string }) => void; auto_select?: boolean; cancel_on_tap_outside?: boolean }): void;
  renderButton(el: HTMLElement, opts: Record<string, unknown>): void;
  prompt(): void;
  disableAutoSelect(): void;
}
function gsi(): GsiId | null {
  const g = (window as unknown as { google?: { accounts?: { id?: GsiId } } }).google;
  return g?.accounts?.id ?? null;
}

/** Decode the JWT payload (browser-side, for display only — never trusted for auth). */
function decodeJwt(token: string): (GoogleUser & { exp: number }) | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const p = JSON.parse(json) as { sub: string; name?: string; picture?: string; email?: string; exp: number };
    return { sub: p.sub, name: p.name ?? 'Player', picture: p.picture ?? '', email: p.email, exp: (p.exp ?? 0) * 1000 };
  } catch {
    return null;
  }
}

type Stored = { mode: 'google'; token: string } | { mode: 'local'; token: string; name: string; sub: string };

/**
 * Single sign-in authority for the game: Google one-tap OR a simple local username+password
 * account (so everyone can sync + play tournaments, not only Google users). Exposes a uniform
 * `authBody()` consumed by the cloud-save and Arena APIs. The merge/orchestration lives in
 * SaveManager, which subscribes via `init(onChange)`.
 */
export class CloudSync {
  private static _mode: 'google' | 'local' | null = null;
  private static _token: string | null = null;
  private static _user: GoogleUser | null = null;
  private static _onChange: ((u: GoogleUser | null) => void) | null = null;
  private static scriptLoading: Promise<boolean> | null = null;

  /** Whether Google sign-in is configured (local accounts always work). */
  static get googleEnabled(): boolean {
    return GOOGLE_CLIENT_ID.length > 0;
  }
  static get isSignedIn(): boolean {
    return !!this._token && !!this._user;
  }
  static get user(): GoogleUser | null {
    return this._user;
  }

  /** The auth payload to merge into authed API request bodies, or null when signed out. */
  static authBody(): AuthBody | null {
    if (!this._token) return null;
    return this._mode === 'google' ? { idToken: this._token } : { token: this._token };
  }

  /** Load the GSI client script once. Resolves false if it can't load (offline). */
  private static ensureScript(): Promise<boolean> {
    if (gsi()) return Promise.resolve(true);
    if (this.scriptLoading) return this.scriptLoading;
    this.scriptLoading = new Promise<boolean>((resolve) => {
      const existing = document.querySelector('script[data-gsi]');
      if (existing) { existing.addEventListener('load', () => resolve(!!gsi())); return; }
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true; s.defer = true; s.dataset.gsi = '1';
      s.onload = () => resolve(!!gsi());
      s.onerror = () => resolve(false);
      document.head.appendChild(s);
    });
    return this.scriptLoading;
  }

  /**
   * Initialize sign-in. `onChange` fires with the user on sign-in and null on sign-out.
   * Restores a saved session first (instant returning-user), then wires Google one-tap if enabled.
   */
  static async init(onChange: (u: GoogleUser | null) => void): Promise<void> {
    this._onChange = onChange;
    this.restore();
    // On CrazyGames, silently provision a backend account so online features (Arena) work WITHOUT a
    // login screen — the session token lives in the Data Module, tied to the CrazyGames user and
    // synced across their devices. No Google, no manual login needed on the portal.
    if (CrazyGames.hasData && !this.isSignedIn) {
      await this.autoProvision();
    }
    if (!this.googleEnabled) return;
    const ok = await this.ensureScript();
    const id = ok ? gsi() : null;
    if (!id) return;
    id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (r) => this.onGoogleCredential(r.credential),
      auto_select: true,
      cancel_on_tap_outside: true,
    });
    // Silent re-auth on open: if we're signed out but this device has used Google before (token
    // simply expired since last visit), One Tap with auto_select re-issues a fresh ID token with
    // no UI. This keeps returning Google users signed in across refreshes/deploys.
    if (!this.isSignedIn && this.usedGoogleBefore()) {
      try { id.prompt(); } catch { /* ignore */ }
    }
  }

  private static usedGoogleBefore(): boolean {
    try { return localStorage.getItem(GEVER_KEY) === '1'; } catch { return false; }
  }

  /** CrazyGames: create a hidden local account once, so the player is signed in to our backend
   * (Arena, leaderboards) with no UI. The token is persisted (incl. the Data Module) so it follows
   * the CrazyGames user across devices. */
  private static async autoProvision(): Promise<void> {
    const rnd = (): string => {
      try { return (globalThis.crypto?.randomUUID?.() ?? '').replace(/-/g, ''); } catch { /* fall through */ }
      return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    };
    const username = ('cg' + rnd()).slice(0, 20);
    const password = rnd() + rnd();
    await this.localAuth('register', username, password); // sets the session + persists the token
  }

  /** Restore a saved session (Google token if still valid, or the long-lived local token). On
   * CrazyGames the Data Module is the cross-device source, so it's preferred over localStorage. */
  private static restore(): void {
    try {
      const raw = (CrazyGames.hasData ? CrazyGames.dataGet(TOKEN_KEY) : null) ?? localStorage.getItem(TOKEN_KEY);
      if (!raw) return;
      const s = JSON.parse(raw) as Stored;
      if (s.mode === 'google') {
        const claims = decodeJwt(s.token);
        if (claims && claims.exp > Date.now() + 30_000) {
          this._mode = 'google'; this._token = s.token;
          this._user = { sub: claims.sub, name: claims.name, picture: claims.picture, email: claims.email };
        } else { localStorage.removeItem(TOKEN_KEY); }
      } else if (s.mode === 'local' && s.token) {
        this._mode = 'local'; this._token = s.token;
        this._user = { sub: s.sub, name: s.name, picture: '' };
      }
    } catch { /* ignore */ }
  }

  private static persist(s: Stored): void {
    const json = JSON.stringify(s);
    try { localStorage.setItem(TOKEN_KEY, json); } catch { /* ignore */ }
    if (CrazyGames.hasData) CrazyGames.dataSet(TOKEN_KEY, json); // sync the session to the CrazyGames user
  }

  private static onGoogleCredential(token: string): void {
    const claims = decodeJwt(token);
    if (!claims) return;
    this._mode = 'google'; this._token = token;
    this._user = { sub: claims.sub, name: claims.name, picture: claims.picture, email: claims.email };
    this.persist({ mode: 'google', token });
    try { localStorage.setItem(GEVER_KEY, '1'); } catch { /* ignore */ }
    this._onChange?.(this._user);
  }

  // ── Local account (username + password, no email) ──
  /** Register or log in a local account. `mode` picks the endpoint. Returns an error code or null. */
  static async localAuth(mode: 'register' | 'login', username: string, password: string): Promise<string | null> {
    try {
      const res = await fetch(`${BASE}/auth/${mode}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const json = (await res.json().catch(() => null)) as { ok?: boolean; token?: string; uid?: string; username?: string; error?: string } | null;
      if (!res.ok || !json?.ok || !json.token || !json.uid) return json?.error ?? 'error';
      this._mode = 'local'; this._token = json.token;
      this._user = { sub: json.uid, name: json.username ?? username, picture: '' };
      this.persist({ mode: 'local', token: json.token, name: this._user.name, sub: json.uid });
      this._onChange?.(this._user);
      return null;
    } catch {
      return 'network';
    }
  }

  /** Render the official Google button into a container (used by the Account screen). */
  static renderButton(el: HTMLElement): void {
    gsi()?.renderButton(el, { type: 'standard', theme: 'filled_blue', size: 'large', shape: 'pill', text: 'signin_with', width: 240 });
  }

  static promptOneTap(): void {
    try { gsi()?.prompt(); } catch { /* ignore */ }
  }

  static signOut(): void {
    try { gsi()?.disableAutoSelect(); } catch { /* ignore */ }
    this._mode = null; this._token = null; this._user = null;
    // Clear both the session and the "used Google before" flag so an explicit sign-out is NOT
    // silently undone by auto re-auth on the next open.
    try { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(GEVER_KEY); } catch { /* ignore */ }
    if (CrazyGames.hasData) CrazyGames.dataSet(TOKEN_KEY, '');
    this._onChange?.(null);
  }

  // ── Cloud-save transport ──
  static async loadCloud(): Promise<{ blob: string | null; updatedAt: number } | null> {
    const auth = this.authBody();
    if (!auth) return null;
    try {
      const res = await fetch(`${BASE}/save/load`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(auth),
      });
      if (!res.ok) return null;
      const json = (await res.json()) as { blob?: string | null; updatedAt?: number };
      return { blob: json.blob ?? null, updatedAt: json.updatedAt ?? 0 };
    } catch {
      return null;
    }
  }

  static async pushCloud(blob: string): Promise<boolean> {
    const auth = this.authBody();
    if (!auth) return false;
    try {
      const res = await fetch(`${BASE}/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...auth, blob }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }
}
